import { ApiError, NetworkError, type ProblemDetails } from './problem.ts';

export interface ApiClientOptions {
  /** Origin of the API, e.g. `https://api.awj.ae`. No trailing slash. */
  baseUrl: string;
  /**
   * Called when a refresh fails and the session is genuinely over, so the app can send the
   * customer to sign in. Not called for an ordinary 401 that a refresh recovers from.
   */
  onSessionExpired?: () => void;
  /** Server-side rendering has no cookie jar; the caller forwards headers instead. */
  fetchImpl?: typeof fetch;
  defaultHeaders?: Record<string, string>;
}

export interface RequestOptions {
  query?: Record<string, string | number | boolean | undefined | null | (string | number)[]>;
  body?: unknown;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  /** Skips the refresh-and-retry dance. Used by the auth endpoints themselves. */
  skipRefresh?: boolean;
  /** Read the answer as a file rather than as JSON — a PDF export, say. */
  responseType?: 'blob';
}

const CSRF_COOKIE = 'awj_csrf';
const CSRF_HEADER = 'X-CSRF-Token';

/**
 * The typed HTTP client both front ends use.
 *
 * Three things it handles so no call site has to:
 *
 * **Cookies, not headers.** Access and refresh tokens live in HttpOnly cookies, so the client
 * never sees or stores a token — an XSS bug cannot read a session out of it. Every request is
 * sent with `credentials: 'include'`.
 *
 * **CSRF.** The server issues a readable token in a cookie and requires it echoed in a header on
 * every state-changing request. That is done here rather than in each caller. When the API is on
 * another host its cookie is out of `document.cookie`'s reach, so the client also keeps the copy
 * the server sends in the `X-CSRF-Token` response header.
 *
 * **One refresh at a time.** When a request 401s, the client refreshes and retries. If ten
 * requests 401 together — which is exactly what happens when a tab wakes up — they share a
 * single refresh rather than firing ten. That matters beyond efficiency: refresh tokens rotate,
 * so ten concurrent refreshes would present the same spent token repeatedly and trip the
 * server's reuse detection, revoking the whole session.
 */
export class ApiClient {
  private readonly baseUrl: string;
  private readonly fetchImpl: typeof fetch;
  private readonly defaultHeaders: Record<string, string>;
  private readonly onSessionExpired?: () => void;

  /** The in-flight refresh, shared by everything waiting on it. */
  private refreshing: Promise<boolean> | null = null;

  /** The CSRF token from the last response that carried one. */
  private csrfToken: string | undefined;

  constructor(options: ApiClientOptions) {
    this.baseUrl = options.baseUrl.replace(/\/$/, '');
    this.fetchImpl = options.fetchImpl ?? globalThis.fetch.bind(globalThis);
    this.defaultHeaders = options.defaultHeaders ?? {};
    this.onSessionExpired = options.onSessionExpired;
  }

  get<T>(path: string, options: RequestOptions = {}): Promise<T> {
    return this.request<T>('GET', path, options);
  }

  post<T>(path: string, options: RequestOptions = {}): Promise<T> {
    return this.request<T>('POST', path, options);
  }

  put<T>(path: string, options: RequestOptions = {}): Promise<T> {
    return this.request<T>('PUT', path, options);
  }

  delete<T>(path: string, options: RequestOptions = {}): Promise<T> {
    return this.request<T>('DELETE', path, options);
  }

  /** Multipart upload — the browser sets the boundary, so Content-Type is left alone. */
  async upload<T>(path: string, file: File, fieldName = 'file'): Promise<T> {
    const form = new FormData();
    form.append(fieldName, file);

    // `this.url(path)`, like every other verb: `send` takes a resolved address, and handing it a
    // bare path posts the file to whatever origin the panel itself is served from.
    return this.send<T>('POST', this.url(path), { body: form }, { ...this.defaultHeaders });
  }

  private async request<T>(
    method: string,
    path: string,
    options: RequestOptions,
  ): Promise<T> {
    const headers: Record<string, string> = { ...this.defaultHeaders, ...options.headers };
    let body: BodyInit | undefined;

    if (options.body !== undefined) {
      headers['Content-Type'] = 'application/json';
      body = JSON.stringify(options.body);
    }

    return this.send<T>(method, this.url(path, options.query), { body, signal: options.signal }, headers, options);
  }

  private async send<T>(
    method: string,
    url: string,
    init: { body?: BodyInit; signal?: AbortSignal },
    headers: Record<string, string>,
    options: RequestOptions = {},
    csrfRetried = false,
  ): Promise<T> {
    const csrf = this.currentCsrfToken();

    if (csrf && method !== 'GET' && method !== 'HEAD') {
      headers[CSRF_HEADER] = csrf;
    }

    let response: Response;

    try {
      response = await this.fetchImpl(url, {
        method,
        headers,
        body: init.body,
        signal: init.signal,
        // The whole auth scheme depends on this: without it the browser sends no cookies and
        // every authenticated request is anonymous.
        credentials: 'include',
      });
    } catch (error) {
      // AbortError is the caller's own doing; it should surface as itself.
      if (error instanceof DOMException && error.name === 'AbortError') {
        throw error;
      }

      throw new NetworkError(error);
    }

    this.rememberCsrfToken(response);

    // A first write from a fresh client goes out before it has seen a token. The refusal carries
    // one, so try once more with it.
    if (response.status === 403 && !csrfRetried && this.csrfToken && this.csrfToken !== csrf) {
      return this.send<T>(method, url, init, headers, options, true);
    }

    if (response.status === 401 && !options.skipRefresh) {
      const refreshed = await this.refreshOnce();

      if (refreshed) {
        // One retry only. A second 401 after a successful refresh means the caller genuinely
        // lacks access, and retrying again would loop.
        return this.send<T>(method, url, init, headers, { ...options, skipRefresh: true }, csrfRetried);
      }

      this.onSessionExpired?.();
    }

    if (!response.ok) {
      throw new ApiError(response.status, await readProblem(response));
    }

    return options.responseType === 'blob' ? ((await response.blob()) as T) : readBody<T>(response);
  }

  /**
   * Refreshes the session, coalescing concurrent callers onto one attempt.
   *
   * The coalescing is not an optimisation. Refresh tokens rotate and the server detects reuse by
   * revoking the whole family, so several parallel refreshes would present the same spent token
   * and sign the customer out entirely.
   */
  private refreshOnce(): Promise<boolean> {
    this.refreshing ??= (async () => {
      try {
        const csrf = this.currentCsrfToken();
        const response = await this.fetchImpl(`${this.baseUrl}/api/v1/auth/refresh`, {
          method: 'POST',
          // The default headers too: they say which app's session this is (the admin's has
          // cookies of its own), and a refresh of the wrong one signs nobody back in.
          headers: { ...this.defaultHeaders, ...(csrf ? { [CSRF_HEADER]: csrf } : {}) },
          credentials: 'include',
        });

        this.rememberCsrfToken(response);

        return response.ok;
      } catch {
        return false;
      } finally {
        // Cleared so the *next* 401 starts a fresh attempt rather than reusing this result.
        this.refreshing = null;
      }
    })();

    return this.refreshing;
  }

  /** The cookie when this page can read it (same host as the API), else the last header copy. */
  private currentCsrfToken(): string | undefined {
    return readCookie(CSRF_COOKIE) ?? this.csrfToken;
  }

  private rememberCsrfToken(response: Response): void {
    const issued = response.headers?.get(CSRF_HEADER);

    if (issued) {
      this.csrfToken = issued;
    }
  }

  private url(path: string, query?: RequestOptions['query']): string {
    const url = new URL(`${this.baseUrl}${path.startsWith('/') ? path : `/${path}`}`);

    for (const [key, value] of Object.entries(query ?? {})) {
      if (value === undefined || value === null || value === '') {
        continue;
      }

      // Repeated key for arrays, which is what ASP.NET Core model binding expects.
      if (Array.isArray(value)) {
        for (const entry of value) {
          url.searchParams.append(key, String(entry));
        }
      } else {
        url.searchParams.set(key, String(value));
      }
    }

    return url.toString();
  }
}

async function readProblem(response: Response): Promise<ProblemDetails> {
  try {
    const problem = (await response.json()) as ProblemDetails;

    return typeof problem === 'object' && problem !== null ? problem : { status: response.status };
  } catch {
    // A gateway timeout or a proxy error page is not JSON. Better a generic problem than a
    // parse exception swallowing the real status.
    return { status: response.status, title: response.statusText };
  }
}

async function readBody<T>(response: Response): Promise<T> {
  // 204 is the success shape for every command endpoint in this API.
  if (response.status === 204 || response.headers.get('content-length') === '0') {
    return undefined as T;
  }

  const contentType = response.headers.get('content-type') ?? '';

  return contentType.includes('json') ? ((await response.json()) as T) : ((await response.text()) as T);
}

/**
 * Reads a cookie by name. Returns undefined outside the browser, which is correct for
 * server-side rendering: there is no CSRF cookie to echo, and those requests are reads.
 */
function readCookie(name: string): string | undefined {
  if (typeof document === 'undefined') {
    return undefined;
  }

  const match = document.cookie
    .split('; ')
    .find((entry) => entry.startsWith(`${name}=`));

  return match ? decodeURIComponent(match.slice(name.length + 1)) : undefined;
}
